import React from "react"
import autobind from "class-autobind"
import strings from "../../localization/strings"
import { Paper, FontIcon, Toggle, CircularProgress, IconMenu, IconButton, MenuItem } from "material-ui"
import AccountStore from "../../stores/AccountStore"
import HierarchySettingsStore from "../../stores/HierarchySettingsStore"
import ExceptionPunchSetsTable from "./ExceptionPunchSetsTable"
import {
    IUIUser, IPunchExceptionFilterDto, IPunchSetRecordDtoSearch, IPunchSetRecordDto, IUserDocument, IPunchSetDocument,
    IPunchDocument, EExceptionUITypes, IPunchSetRecordBySupervisorDto, IPunchSetRecordBySupervisorSearchDto, IPunchBundle,
    ECheckResultForRemovedPunchData, IDate, ITimeoffRequestDocument, IRecord, ERecordPageType, ERecordSearchFilter,
    EExceptionReviewDialogExceptionType, EExceptionReviewDialogReviewType, IUserFilter
} from "../../models"
import * as api from "../../api"
import AddPunchDialog from "../punch/AddPunchDialog"
import moment from "moment"
import * as ExceptionFilterActions from "../../actions/ExceptionFilterActions"
import ExceptionFilterStore from "../../stores/ExceptionFilterStore"
import ExceptionPunchSetsBySupervisorTable from "./ExceptionPunchSetsBySupervisorTable"
import ExceptionFilter from "./ExceptionFilter"
import AppStateStore from "../../stores/AppStateStore"
import * as exceptionHelper from "../../helpers/exceptionHelper"
import * as punchHelper from "../../helpers/punchHelper"
import * as punchActions from "../../actions/PunchActions"
import { grey300 } from "material-ui/styles/colors"
import LeaveTrackingLiveView from "../leave_tracking/list_view/LeaveTrackingLiveView"
import RecordTable from "../record/RecordTable"
import * as approveActions from "../../actions/ApproveActions"
import _ from "lodash"
import * as timeHelper from "../../helpers/timeHelper"
import * as reportAction from "../../actions/ReportAction"

interface IProps {
    isPayroll: boolean
    payGroupIds: string[]
    referenceDate: IDate
    localStorageKey: string

    // Timecard page
    filterDirectReportsOnly: boolean
    filterByPunchHierarchy: boolean
    filterByDefaultHierarchy: boolean
    selectedFilterTypes?: EExceptionUITypes[]
    handleSelectSelectedFilterTypes?(selectedFilterTypes: EExceptionUITypes[]): void
    userFilter?: IUserFilter

    // Payroll page
    viewPayrollByJob: boolean
}
interface IState {
    loading: boolean
    exceptionPunchSetRecordsBySupervisorDto: IPunchSetRecordBySupervisorDto[]
    expandedRowBySupervisorId: string
    exceptionPunchSetRecordsDto: any[]
    unapprovedTimeoffs: ITimeoffRequestDocument[]
    suspensePunches: IRecord[]
    timeFraudPunches: IRecord[]
    page: number
    limit: number
    expanded: boolean
    showReviewed: boolean
    showFilterTypes: EExceptionUITypes[]
    selectedFilterTypes: EExceptionUITypes[]
    allExceptionCount: number
    allExceptionCountForFilter: number
    missPunch: number
    noEmpApprove: number
    noSupApprove: number
    bioException: number
    locException: number
    scheduleMissingInPunchException: number
    scheduleEarlyInPunchException: number
    scheduleLateInPunchException: number
    scheduleEarlyOutPunchException: number
    scheduleLateOutPunchException: number
    scheduleMissingBreakException: number
    scheduleLongBreakException: number
    scheduleNoShiftException: number
    prePunchScreeningException: number
    unapprovedTimeoffRequestCount: number
    suspensePunchCount: number
    timeFraudPunchCount: number
    openPunchDialog: boolean
    addPunchUser?: IUserDocument
    punchSetRecordDtoForAddPunch?: IPunchSetRecordDto
    groupBySupervisor: boolean
}

export default class ExceptionPunchSets extends React.Component<IProps, IState> {
    private limitOptions: number[] = [10, 20, 50]
    constructor(props: IProps) {
        super(props)
        this.state = {
            loading: true,
            exceptionPunchSetRecordsBySupervisorDto: [],
            expandedRowBySupervisorId: null,
            exceptionPunchSetRecordsDto: [],
            unapprovedTimeoffs: [],
            suspensePunches: [],
            timeFraudPunches: [],
            page: 0,
            limit: this.limitOptions[0],
            expanded: false,
            showReviewed: false,
            showFilterTypes: exceptionHelper.getExceptionFilterShowItemsByStorage(),
            selectedFilterTypes: props.selectedFilterTypes || exceptionHelper.getSelectedExceptionFilterTypesByStorage(props.localStorageKey),
            allExceptionCount: null,
            allExceptionCountForFilter: 0,
            missPunch: null,
            noEmpApprove: null,
            noSupApprove: null,
            bioException: null,
            locException: null,
            scheduleMissingInPunchException: null,
            scheduleEarlyInPunchException: null,
            scheduleLateInPunchException: null,
            scheduleEarlyOutPunchException: null,
            scheduleLateOutPunchException: null,
            scheduleMissingBreakException: null,
            scheduleLongBreakException: null,
            scheduleNoShiftException: null,
            prePunchScreeningException: null,
            unapprovedTimeoffRequestCount: null,
            suspensePunchCount: null,
            timeFraudPunchCount: null,
            openPunchDialog: false,
            groupBySupervisor: props.isPayroll && !props.viewPayrollByJob && AccountStore.getSupervisorDirectReportEnabled()
        }
        autobind(this)
    }

    componentDidMount() {
        ExceptionFilterStore.on("refresh_exception_filter", this.exceptionStoreChangeEvent)
        HierarchySettingsStore.on("global_change", this.handleChangeGlobalHierarchy)
        this.getPunchSetExceptions(this.props)
    }

    componentDidUpdate(prevProps: IProps) {
        // Check for userFilter changes
        if (!_.isEqual(prevProps.userFilter, this.props.userFilter)) {
            this.setState({ loading: true }, () =>
                this.reLoadData(this.props)
            )
            return
        }

        // Check other prop changes
        if (
            !_.isEqual(prevProps.payGroupIds, this.props.payGroupIds) ||
            prevProps.referenceDate !== this.props.referenceDate ||
            prevProps.filterDirectReportsOnly !== this.props.filterDirectReportsOnly ||
            prevProps.filterByDefaultHierarchy !== this.props.filterByDefaultHierarchy ||
            prevProps.filterByPunchHierarchy !== this.props.filterByPunchHierarchy
        ) {
            this.setState({ loading: true }, () =>
                this.reLoadData(this.props)
            )
        }
    }

    componentWillUnmount() {
        ExceptionFilterStore.removeListener("refresh_exception_filter", this.exceptionStoreChangeEvent)
        HierarchySettingsStore.removeListener("global_change", this.handleChangeGlobalHierarchy)
    }

    handleChangeGlobalHierarchy() {
        this.setState({ loading: true }, () =>
            this.reLoadData(this.props)
        )
    }

    exceptionStoreChangeEvent() {
        ExceptionFilterStore.getRefreshExceptionFilter() && this.reLoadData(this.props)
    }

    reLoadData(props: IProps) {
        this.setState({
            exceptionPunchSetRecordsDto: [],
            unapprovedTimeoffs: [],
            suspensePunches: [],
            timeFraudPunches: [],
            page: 0,
            allExceptionCountForFilter: 0
        }, () => this.getPunchSetExceptions(props))
    }

    getExceptionFilterObj(props: IProps): IPunchExceptionFilterDto {
        return {
            ExceptionCountFilters: exceptionHelper.getExceptionTypes(this.state.showFilterTypes),
            ExceptionFilters: exceptionHelper.getExceptionTypes(this.state.selectedFilterTypes),
            ShowAll: this.state.showReviewed,
            PayGroupIds: props.payGroupIds,
            UserFilter: props.userFilter
        }
    }
    async getPunchSetExceptions(props: IProps) {
        const response = await api.getAccountPunchSetExceptions(
            AccountStore.getCurrentAccount().Id,
            this.state.groupBySupervisor,
            moment(props.referenceDate).utc(true).format(),
            props.isPayroll,
            HierarchySettingsStore.getGlobalLevelItemFilterId(),
            this.state.page / this.state.limit,
            this.state.limit,
            props.filterDirectReportsOnly,
            props.filterByPunchHierarchy,
            props.filterByDefaultHierarchy,
            props.viewPayrollByJob,
            this.getExceptionFilterObj(props)
        )

        if (this.state.groupBySupervisor) {
            const exceptionPunchSetRecordBySupervisorSearchDto: IPunchSetRecordBySupervisorSearchDto = response.data
            this.setState({
                loading: false,
                allExceptionCount: exceptionPunchSetRecordBySupervisorSearchDto.TotalDocumentCount,
                exceptionPunchSetRecordsBySupervisorDto: exceptionPunchSetRecordBySupervisorSearchDto.Documents,
                allExceptionCountForFilter: exceptionPunchSetRecordBySupervisorSearchDto.TotalFilterDocumentCount,
                missPunch: exceptionPunchSetRecordBySupervisorSearchDto.MissingPunchTotal,
                noEmpApprove: exceptionPunchSetRecordBySupervisorSearchDto.NoEmployeeApprovalTotal,
                noSupApprove: exceptionPunchSetRecordBySupervisorSearchDto.NoSupervisorApprovalTotal,
                bioException: exceptionPunchSetRecordBySupervisorSearchDto.BiometricTotal,
                locException: exceptionPunchSetRecordBySupervisorSearchDto.LocationTotal,
                scheduleMissingInPunchException: exceptionPunchSetRecordBySupervisorSearchDto.MissingInTotal,
                scheduleEarlyInPunchException: exceptionPunchSetRecordBySupervisorSearchDto.EarlyInTotal,
                scheduleLateInPunchException: exceptionPunchSetRecordBySupervisorSearchDto.LateInTotal,
                scheduleEarlyOutPunchException: exceptionPunchSetRecordBySupervisorSearchDto.EarlyOutTotal,
                scheduleLateOutPunchException: exceptionPunchSetRecordBySupervisorSearchDto.LateOutTotal,
                scheduleMissingBreakException: exceptionPunchSetRecordBySupervisorSearchDto.MissingBreakTotal,
                scheduleLongBreakException: exceptionPunchSetRecordBySupervisorSearchDto.LongBreakTotal,
                scheduleNoShiftException: exceptionPunchSetRecordBySupervisorSearchDto.NoShiftTotal,
                prePunchScreeningException: exceptionPunchSetRecordBySupervisorSearchDto.PrePunchScreeningTotal,
                unapprovedTimeoffRequestCount: exceptionPunchSetRecordBySupervisorSearchDto.UnapprovedTimeoffTotal,
                suspensePunchCount: exceptionPunchSetRecordBySupervisorSearchDto.SuspensePunchTotal,
                timeFraudPunchCount: exceptionPunchSetRecordBySupervisorSearchDto.TimeFraudPunchTotal
            })
        }
        else {
            const exceptionPunchSetRecordDtoSearch: IPunchSetRecordDtoSearch = response.data
            const punchSets = exceptionPunchSetRecordDtoSearch.PunchSetDocuments as any
            const shiftExceptions = exceptionPunchSetRecordDtoSearch.ShiftExceptionDocuments as any
            this.setState({
                loading: false,
                allExceptionCount: exceptionPunchSetRecordDtoSearch.TotalDocumentCount,
                exceptionPunchSetRecordsDto: punchSets.concat(shiftExceptions),
                allExceptionCountForFilter: exceptionPunchSetRecordDtoSearch.TotalFilterDocumentCount,
                missPunch: exceptionPunchSetRecordDtoSearch.MissingPunchTotal,
                noEmpApprove: exceptionPunchSetRecordDtoSearch.NoEmployeeApprovalTotal,
                noSupApprove: exceptionPunchSetRecordDtoSearch.NoSupervisorApprovalTotal,
                bioException: exceptionPunchSetRecordDtoSearch.BiometricTotal,
                locException: exceptionPunchSetRecordDtoSearch.LocationTotal,
                scheduleMissingInPunchException: exceptionPunchSetRecordDtoSearch.MissingInTotal,
                scheduleEarlyInPunchException: exceptionPunchSetRecordDtoSearch.EarlyInTotal,
                scheduleLateInPunchException: exceptionPunchSetRecordDtoSearch.LateInTotal,
                scheduleEarlyOutPunchException: exceptionPunchSetRecordDtoSearch.EarlyOutTotal,
                scheduleLateOutPunchException: exceptionPunchSetRecordDtoSearch.LateOutTotal,
                scheduleMissingBreakException: exceptionPunchSetRecordDtoSearch.MissingBreakTotal,
                scheduleLongBreakException: exceptionPunchSetRecordDtoSearch.LongBreakTotal,
                scheduleNoShiftException: exceptionPunchSetRecordDtoSearch.NoShiftTotal,
                prePunchScreeningException: exceptionPunchSetRecordDtoSearch.PrePunchScreeningTotal,
                unapprovedTimeoffRequestCount: exceptionPunchSetRecordDtoSearch.UnapprovedTimeoffTotal,
                unapprovedTimeoffs: exceptionPunchSetRecordDtoSearch.UnapprovedTimeoffs,
                suspensePunchCount: exceptionPunchSetRecordDtoSearch.SuspensePunchTotal,
                suspensePunches: exceptionPunchSetRecordDtoSearch.SuspensePunches,
                timeFraudPunchCount: exceptionPunchSetRecordDtoSearch.TimeFraudPunchTotal,
                timeFraudPunches: exceptionPunchSetRecordDtoSearch.TimeFraudPunches
            })
        }
    }

    handleSelectFilterType(clickedFilterType: EExceptionUITypes) {
        let selectedFilterTypes = this.state.selectedFilterTypes.slice()

        if (clickedFilterType === EExceptionUITypes.Timeoff) {
            selectedFilterTypes = selectedFilterTypes.includes(clickedFilterType) ? [] : [EExceptionUITypes.Timeoff]
        }
        else if (clickedFilterType === EExceptionUITypes.SuspensePunch) {
            selectedFilterTypes = selectedFilterTypes.includes(clickedFilterType) ? [] : [EExceptionUITypes.SuspensePunch]
        }
        else if (clickedFilterType === EExceptionUITypes.TimeFraud) {
            selectedFilterTypes = selectedFilterTypes.includes(clickedFilterType) ? [] : [EExceptionUITypes.TimeFraud]
        }
        else {
            selectedFilterTypes = selectedFilterTypes.filter(i => ![EExceptionUITypes.Timeoff, EExceptionUITypes.SuspensePunch, EExceptionUITypes.TimeFraud].includes(i))
            if (selectedFilterTypes.includes(clickedFilterType)) {
                selectedFilterTypes = selectedFilterTypes.filter(i => i !== clickedFilterType)
            }
            else {
                selectedFilterTypes.push(clickedFilterType)
            }
        }

        this.setState({ selectedFilterTypes, loading: true }, () =>
            this.reLoadData(this.props)
        )
        this.props.handleSelectSelectedFilterTypes &&
            this.props.handleSelectSelectedFilterTypes(selectedFilterTypes)

        localStorage.setItem(`findd_${this.props.localStorageKey}`, selectedFilterTypes.length ? selectedFilterTypes.toString() : "none")
    }

    async handleOpenAddPunchDialog(punchSetRecordDtoForAddPunch: IPunchSetRecordDto) {
        const punchSet = punchSetRecordDtoForAddPunch.PunchSet
        const result = await punchHelper.checkRemovedDataForPunch(punchSet.UserId, punchSet.HierarchyItemId, punchSet.ShiftId, punchSet.JobId, punchSet.PhaseId, punchSet.PositionId)
        if (result !== ECheckResultForRemovedPunchData.Valid) return
        this.setState({
            openPunchDialog: true,
            punchSetRecordDtoForAddPunch
        })
        api.getUser(punchSetRecordDtoForAddPunch.PunchIn.UserId).then(res => this.setState({ addPunchUser: res.data }))
    }

    addPunch(userId: string, punchBundle: IPunchBundle) {
        this.setState({ openPunchDialog: false, punchSetRecordDtoForAddPunch: null, addPunchUser: null })
        punchActions.addPunch(userId, punchBundle, true, () => {
            ExceptionFilterActions.refreshPage()
            this.reLoadData(this.props)
        })
    }

    handleApprovePunchSet(punchSetRecordDto: IPunchSetRecordDto) {
        AppStateStore.setConfirmDialogContent({
            title: strings.Approve_PunchSet,
            content: strings.Are_you_sure_you_want_to_approve_the_punchset,
            confirm: () => this.approvePunchSet(punchSetRecordDto.PunchSet)
        })
    }

    approvePunchSet(punchSet: IPunchSetDocument) {
        approveActions.approvePunchSet(punchSet,
            () => {
                ExceptionFilterActions.refreshPage()
                this.reLoadData(this.props)
            }
        )
    }

    handleSetPunchExceptionReviewDialogContent(clickedExceptionType: EExceptionReviewDialogExceptionType, exceptionPunchId: string) {
        let punches: IPunchDocument[] = []
        this.state.groupBySupervisor ?
            this.state.exceptionPunchSetRecordsBySupervisorDto.forEach(groupItem => groupItem.PunchSetDocuments.forEach(item => {
                item.PunchIn && punches.push(item.PunchIn)
                item.PunchOut && punches.push(item.PunchOut)
            })) :
            this.state.exceptionPunchSetRecordsDto.forEach(item => {
                item.PunchIn && punches.push(item.PunchIn)
                item.PunchOut && punches.push(item.PunchOut)
            })
        AppStateStore.setPunchExceptionReviewDialogContent({
            exceptionPunchId,
            clickedExceptionType,
            punches,
            availableExceptionTypes: [EExceptionReviewDialogExceptionType.Biometric, EExceptionReviewDialogExceptionType.Location],
            reviewedType: this.state.showReviewed ? EExceptionReviewDialogReviewType.All : EExceptionReviewDialogReviewType.NotReviewed,
            closeCallback: () => {
                this.setState({ loading: true })
                this.reLoadData(this.props)
            }
        })
    }

    handleDownloadReport() {
        const response = reportAction.getExceptionsReport(
            AccountStore.getCurrentAccount().Id,
            moment(this.props.referenceDate).utc(true).format(),
            this.props.isPayroll,
            HierarchySettingsStore.getGlobalLevelItemFilterId(),
            this.props.filterDirectReportsOnly,
            this.props.filterByPunchHierarchy,
            this.props.filterByDefaultHierarchy,
            this.props.viewPayrollByJob,
            this.getExceptionFilterObj(this.props)
        )
    }

    render() {
        const downloadReport = (
            <IconMenu
                style={{ padding: 0 }}
                iconButtonElement={
                    <IconButton
                        data-intercom-target={"PunchSet-Exceptions-Report"}
                        tooltip={strings.Exceptions_Reports}
                        style={{ padding: 2, width: 35, height: 35 }}
                    >
                        <FontIcon className="fa fa-file-text itemActionButton" style={{ fontSize: 16 }} />
                    </IconButton>
                }
            >
                <MenuItem
                    data-intercom-target={"unchSet-Exceptions-Report-Download"}
                    primaryText={strings.Download}
                    leftIcon={<FontIcon className="fa fa-download" />}
                    onTouchTap={this.handleDownloadReport}
                />
            </IconMenu>
        )
        return (
            <Paper data-intercom-target={"Exception-AllExceptionsExapansionPanel"} zDepth={1} className="page-paper" style={{ marginTop: 0, marginBottom: 20, paddingTop: 0 }} id="aaaaa">
                <div style={{ position: "absolute", right: 15, top: 28, fontSize: 20 }}>
                    <i
                        data-intercom-target={"Exception-AllExceptions-Btn-Expand"}
                        className={"fa " + (this.state.expanded ? "fa-chevron-up" : "fa-chevron-down") + " itemActionButton"}
                        onClick={() => this.setState({ expanded: !this.state.expanded, showReviewed: this.state.expanded ? false : this.state.showReviewed })}
                    />
                </div>
                {this.state.expanded ?
                    <div>
                        <div style={{ height: 80, display: "flex" }}>
                            <div style={{ width: 200, textAlign: "center", paddingTop: 30, verticalAlign: "top", position: "relative" }}>
                                <div style={{ position: "absolute", top: 0, right: 0 }}>{downloadReport}</div>
                                <div style={{ fontSize: 15, fontWeight: "bold" }}>
                                    <FontIcon className={this.state.showReviewed ? "fa fa-check" : "fa fa-warning"} style={{ fontSize: 20, color: this.state.showReviewed ? "rgb(76, 175, 80)" : "#FFC400", marginRight: 5 }} />
                                    <span>
                                        {`${strings.All_Exceptions}: ${this.state.allExceptionCount === null ? "" : this.state.allExceptionCount}`}
                                    </span>
                                </div>
                                {(!this.state.selectedFilterTypes.includes(EExceptionUITypes.Timeoff) && !this.state.selectedFilterTypes.includes(EExceptionUITypes.SuspensePunch)) &&
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: 10 }}>
                                        <Toggle
                                            data-intercom-target={"Exception-Toggle-ShowReviewed"}
                                            label={strings.Show_Reviewed}
                                            toggled={this.state.showReviewed}
                                            style={{ width: 170 }}
                                            onToggle={(e, value) => this.setState({ showReviewed: value, loading: true }, () => this.reLoadData(this.props))}
                                        />
                                    </div>
                                }
                                {AccountStore.getSupervisorDirectReportEnabled() && this.props.isPayroll && !this.props.viewPayrollByJob &&
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: 0 }}>
                                        <Toggle
                                            data-intercom-target={"Exception-Toggle-GroupBySupervisor"}
                                            label={strings.Group_By_Supervisor}
                                            toggled={this.state.groupBySupervisor}
                                            style={{ width: 170 }}
                                            onToggle={(e, value) => this.setState({ groupBySupervisor: value, loading: true }, () => this.reLoadData(this.props))}
                                        />
                                    </div>
                                }
                            </div>
                            <div style={{ paddingTop: 10, width: "calc(100% - 250px)" }} >
                                <ExceptionFilter
                                    showFilterTypes={this.state.showFilterTypes}
                                    selectedFilterTypes={this.state.selectedFilterTypes}
                                    missPunch={this.state.missPunch}
                                    noEmpApprove={this.state.noEmpApprove}
                                    noSupApprove={this.state.noSupApprove}
                                    bioException={this.state.bioException}
                                    locException={this.state.locException}
                                    prePunchScreeningException={this.state.prePunchScreeningException}
                                    scheduleMissingInPunchException={this.state.scheduleMissingInPunchException}
                                    scheduleEarlyInPunchException={this.state.scheduleEarlyInPunchException}
                                    scheduleLateInPunchException={this.state.scheduleLateInPunchException}
                                    scheduleEarlyOutPunchException={this.state.scheduleEarlyOutPunchException}
                                    scheduleLateOutPunchException={this.state.scheduleLateOutPunchException}
                                    scheduleMissingBreakException={this.state.scheduleMissingBreakException}
                                    scheduleLongBreakException={this.state.scheduleLongBreakException}
                                    scheduleNoShiftException={this.state.scheduleNoShiftException}
                                    timeoff={this.state.unapprovedTimeoffRequestCount}
                                    suspensePunch={this.state.suspensePunchCount}
                                    timeFraud={this.state.timeFraudPunchCount}
                                    handleShowFilterTypes={showFilterTypes => this.setState({ showFilterTypes })}
                                    handleSelectFilterType={this.handleSelectFilterType}
                                />
                            </div>
                            <div style={{ width: 50 }} />
                        </div>
                        <div style={{ padding: 20 }}>
                            {this.state.loading ?
                                <div style={{ textAlign: "center" }}>
                                    <CircularProgress />
                                </div> :
                                this.state.groupBySupervisor ?
                                    this.state.exceptionPunchSetRecordsBySupervisorDto.length === 0 ?
                                        <h2 style={{ textAlign: "center", marginTop: 25 }}>{strings.No_Exception_found}</h2> :
                                        <ExceptionPunchSetsBySupervisorTable
                                            selectedFilterTypes={this.state.selectedFilterTypes}
                                            exceptionPunchSetRecordsBySupervisorDto={this.state.exceptionPunchSetRecordsBySupervisorDto}
                                            expandedRowSupervisorId={this.state.expandedRowBySupervisorId}
                                            handleUpdateExpandedRowSupervisorId={expandedRowBySupervisorId =>
                                                this.setState({ expandedRowBySupervisorId: expandedRowBySupervisorId !== this.state.expandedRowBySupervisorId && expandedRowBySupervisorId })
                                            }
                                            onAddPunch={this.handleOpenAddPunchDialog}
                                            onApprovePunchSet={this.handleApprovePunchSet}
                                            handleShowExceptionDailog={this.handleSetPunchExceptionReviewDialogContent}
                                            reloadData={() => this.reLoadData(this.props)}
                                        /> :
                                    this.state.selectedFilterTypes.includes(EExceptionUITypes.Timeoff) ?
                                        this.state.unapprovedTimeoffs.length === 0 ?
                                            <h2 style={{ textAlign: "center", marginTop: 25 }}>{strings.No_unapproved_timeoff_request_found}</h2> :
                                            <LeaveTrackingLiveView
                                                unapprovedException
                                                showStatuActionBar
                                                showEmployeeExternalId
                                                leaveRequests={this.state.unapprovedTimeoffs}
                                                updateStatusCb={() => this.reLoadData(this.props)}
                                            /> :
                                        this.state.selectedFilterTypes.includes(EExceptionUITypes.SuspensePunch) ?
                                            this.state.suspensePunches.length === 0 ?
                                                <h2 style={{ textAlign: "center", marginTop: 25 }}>{strings.No_suspense_punch_found}</h2> :
                                                <RecordTable
                                                    hideExceptionCol
                                                    showEmployeeExternalId
                                                    pageMode={ERecordPageType.TopMenu}
                                                    selectedFilter={ERecordSearchFilter.SuspensePunches}
                                                    records={this.state.suspensePunches}
                                                    reload={init => init && this.reLoadData(this.props)}
                                                /> :
                                            this.state.selectedFilterTypes.includes(EExceptionUITypes.TimeFraud) ?
                                                this.state.timeFraudPunches.length === 0 ?
                                                    <h2 style={{ textAlign: "center", marginTop: 25 }}>{strings.No_time_fraud_punch_found}</h2> :
                                                    <RecordTable
                                                        hideExceptionCol
                                                        showEmployeeExternalId
                                                        pageMode={ERecordPageType.TopMenu}
                                                        selectedFilter={ERecordSearchFilter.All}
                                                        records={this.state.timeFraudPunches}
                                                        reload={init => init && this.reLoadData(this.props)}
                                                    /> :
                                                this.state.exceptionPunchSetRecordsDto.length === 0 ?
                                                    <h2 style={{ textAlign: "center", marginTop: 25 }}>{strings.No_Exception_found}</h2> :
                                                    <ExceptionPunchSetsTable
                                                        selectedFilterTypes={this.state.selectedFilterTypes}
                                                        exceptionPunchSetRecordsDto={this.state.exceptionPunchSetRecordsDto}
                                                        pagination
                                                        total={this.state.allExceptionCountForFilter}
                                                        page={this.state.page}
                                                        limit={this.state.limit}
                                                        limitOptions={this.limitOptions}
                                                        onPageClick={page => this.setState({ page, loading: true }, () => this.getPunchSetExceptions(this.props))}
                                                        onLimitChange={limit => this.setState({ limit, loading: true }, () => this.reLoadData(this.props))}
                                                        onAddPunch={this.handleOpenAddPunchDialog}
                                                        onApprovePunchSet={this.handleApprovePunchSet}
                                                        handleShowExceptionDailog={this.handleSetPunchExceptionReviewDialogContent}
                                                    />
                            }
                        </div>
                    </div> :
                    <div style={{ height: 80, display: "flex" }}>
                        <div style={{ fontSize: 15, fontWeight: "bold", textAlign: "center", width: 200, verticalAlign: "top", paddingTop: 30, position: "relative" }}>
                            <div style={{ position: "absolute", top: 0, right: 0 }}>{downloadReport}</div>
                            <FontIcon className="fa fa-warning" style={{ color: "#FFC400", fontSize: 20, marginRight: 5 }} />
                            <span>
                                {`${strings.All_Exceptions}: ${this.state.allExceptionCount === null ? "" : this.state.allExceptionCount}`}
                            </span>
                        </div>
                        <div style={{ flex: 1, backgroundColor: grey300, width: "calc(100% - 250px)" }}>
                            <ExceptionFilter
                                showFilterTypes={this.state.showFilterTypes}
                                selectedFilterTypes={this.state.selectedFilterTypes}
                                backgroundColor={grey300}
                                missPunch={this.state.missPunch}
                                noEmpApprove={this.state.noEmpApprove}
                                noSupApprove={this.state.noSupApprove}
                                bioException={this.state.bioException}
                                locException={this.state.locException}
                                prePunchScreeningException={this.state.prePunchScreeningException}
                                scheduleMissingInPunchException={this.state.scheduleMissingInPunchException}
                                scheduleEarlyInPunchException={this.state.scheduleEarlyInPunchException}
                                scheduleLateInPunchException={this.state.scheduleLateInPunchException}
                                scheduleEarlyOutPunchException={this.state.scheduleEarlyOutPunchException}
                                scheduleLateOutPunchException={this.state.scheduleLateOutPunchException}
                                scheduleMissingBreakException={this.state.scheduleMissingBreakException}
                                scheduleLongBreakException={this.state.scheduleLongBreakException}
                                scheduleNoShiftException={this.state.scheduleNoShiftException}
                                timeoff={this.state.unapprovedTimeoffRequestCount}
                                suspensePunch={this.state.suspensePunchCount}
                                timeFraud={this.state.timeFraudPunchCount}
                                handleShowFilterTypes={showFilterTypes => this.setState({ showFilterTypes })}
                                handleSelectFilterType={this.handleSelectFilterType}
                            />
                        </div>
                        <div style={{ width: 50 }} />
                    </div>
                }
                {this.state.openPunchDialog && this.state.addPunchUser &&
                    <AddPunchDialog
                        date={moment(
                            timeHelper.keepConvertUTCToLocal(this.state.punchSetRecordDtoForAddPunch.PunchSet.RoundingRuleId ?
                                this.state.punchSetRecordDtoForAddPunch.PunchSet.RoundedPunchInTimeLocal :
                                this.state.punchSetRecordDtoForAddPunch.PunchSet.PunchInTimeLocal
                            )).utc(true).startOf("day").format()
                        }
                        user={this.state.addPunchUser as IUIUser}
                        onRequestClose={() => this.setState({ openPunchDialog: false })}
                        addPunch={this.addPunch}
                        hierarchyId={this.state.punchSetRecordDtoForAddPunch.PunchIn.HierarchyItemId}
                        positionId={this.state.punchSetRecordDtoForAddPunch.PunchIn.PositionId}
                        payClassId={this.state.punchSetRecordDtoForAddPunch.PunchIn.PayClassId}
                        payTypeId={this.state.punchSetRecordDtoForAddPunch.PunchIn.PayTypeId}
                        shiftId={this.state.punchSetRecordDtoForAddPunch.PunchIn.ShiftId}
                        breakId={this.state.punchSetRecordDtoForAddPunch.PunchIn.BreakId}
                        mobileBreakId={this.state.punchSetRecordDtoForAddPunch.PunchIn.MobileBreakId}
                        jobId={this.state.punchSetRecordDtoForAddPunch.PunchIn.JobId}
                        phaseId={this.state.punchSetRecordDtoForAddPunch.PunchIn.PhaseId}
                        equipmentId={this.state.punchSetRecordDtoForAddPunch.PunchIn.EquipmentId}
                        isPunchIn={false}
                        pairedPunchId={this.state.punchSetRecordDtoForAddPunch.PunchIn.Id}
                        payrollHierarchyId={this.state.punchSetRecordDtoForAddPunch.PunchIn.PayrollHierarchyItemId}
                        payrollPayGroupId={this.state.punchSetRecordDtoForAddPunch.PunchIn.PayrollPayGroupId}
                    />
                }
            </Paper>
        )
    }
}
